import { uuidv7 } from "@vmeste/shared";
import type {
  ContentBlob,
  NotificationType,
  CreateListItemRequest,
  CreateListRequest,
  CreatePlaceRequest,
  CreateWishRequest,
  CreateTopicRequest,
  UpdateTopicRequest,
  CreateMessageRequest,
  UpdateMessageRequest,
  CreateEventRequest,
  UpdateEventRequest,
  SetEventParticipationRequest,
  SetReactionRequest,
  CreateTagRequest,
  UpdateTagRequest,
  TagLinkRequest,
  UpdateListItemRequest,
  UpdateListRequest,
  PutPlaceReviewRequest,
  UpdatePlaceRequest,
  UpdateWishRequest,
} from "@vmeste/shared";

/**
 * The offline mutation queue (SPEC §7).
 *
 * Everything a person does goes in here first and is applied to local state
 * optimistically. The queue drains when the network allows. Two properties make
 * that safe:
 *
 *   - Every create carries a client-generated id, so replaying a mutation after
 *     a flaky reconnect returns the original row instead of making a second one.
 *   - Operations on the same row are coalesced, so ticking a box six times on a
 *     train does not send six requests when the tunnel ends.
 */

/**
 * A notification the acting client composed, riding along with its operation.
 *
 * It travels with the operation so it survives being queued offline — the text
 * is written when the person acts, not when the network comes back. And it is
 * encrypted *here*, by the client that did the thing: the server forwards a
 * blob it cannot read, which is what lets a push carry real words in an
 * encrypted space (SPEC §3, decision 3).
 *
 * `type` is carried rather than derived from the operation's kind. Adding an
 * item can mean "a task for you" or nothing at all, depending on whether
 * anybody was assigned, and only the caller knows which.
 */
export interface QueuedNotification {
  type: NotificationType;
  /** What the notification points at, so the tap lands in the right place. */
  targetType: string;
  /**
   * Who it is for, when it is for one person.
   *
   * Left out for anything addressed to the room — a new message, a new wish.
   * Set for anything that names somebody, because "a task for you" said to
   * everyone is a lie to all but one of them.
   */
  userId?: string;
  /** The summary, already through the codec. */
  preview: ContentBlob;
}

export type Operation =
  | { kind: "list.create"; id: string; spaceId: string; body: CreateListRequest }
  | {
      kind: "list.update";
      id: string;
      spaceId: string;
      listId: string;
      body: UpdateListRequest;
    }
  | { kind: "list.delete"; id: string; spaceId: string; listId: string }
  | {
      kind: "item.create";
      id: string;
      spaceId: string;
      body: CreateListItemRequest;
      notify?: QueuedNotification;
    }
  | {
      kind: "item.update";
      id: string;
      spaceId: string;
      itemId: string;
      body: UpdateListItemRequest;
      notify?: QueuedNotification;
    }
  | { kind: "item.delete"; id: string; spaceId: string; itemId: string }
  | {
      kind: "wish.create";
      id: string;
      spaceId: string;
      body: CreateWishRequest;
      notify?: QueuedNotification;
    }
  | {
      kind: "wish.update";
      id: string;
      spaceId: string;
      wishId: string;
      body: UpdateWishRequest;
    }
  | { kind: "wish.delete"; id: string; spaceId: string; wishId: string }
  | {
      kind: "place.create";
      id: string;
      spaceId: string;
      body: CreatePlaceRequest;
      notify?: QueuedNotification;
    }
  | {
      kind: "place.update";
      id: string;
      spaceId: string;
      placeId: string;
      body: UpdatePlaceRequest;
    }
  | { kind: "place.delete"; id: string; spaceId: string; placeId: string }
  | {
      kind: "place.review";
      id: string;
      spaceId: string;
      placeId: string;
      body: PutPlaceReviewRequest;
    }
  | { kind: "place.unreview"; id: string; spaceId: string; placeId: string }
  | { kind: "topic.create"; id: string; spaceId: string; body: CreateTopicRequest }
  | {
      kind: "topic.update";
      id: string;
      spaceId: string;
      topicId: string;
      body: UpdateTopicRequest;
    }
  | { kind: "topic.delete"; id: string; spaceId: string; topicId: string }
  | {
      kind: "message.create";
      id: string;
      spaceId: string;
      body: CreateMessageRequest;
      notify?: QueuedNotification;
    }
  | {
      kind: "message.update";
      id: string;
      spaceId: string;
      messageId: string;
      body: UpdateMessageRequest;
    }
  | { kind: "message.delete"; id: string; spaceId: string; messageId: string }
  /**
   * Your own reaction to one message.
   *
   * Its own kind rather than a `message.update`, because its subject is the pair
   * (message, you) and not the message: reacting must not coalesce with — or be
   * dropped by — an edit to the words queued beside it. And it carries no user,
   * because the route is `…/reactions/me` and the server reads the person off
   * the session, so this operation cannot express anybody else's reaction.
   */
  | {
      kind: "message.react";
      id: string;
      spaceId: string;
      messageId: string;
      body: SetReactionRequest;
    }
  | { kind: "event.create"; id: string; spaceId: string; body: CreateEventRequest }
  | {
      kind: "event.update";
      id: string;
      spaceId: string;
      eventId: string;
      body: UpdateEventRequest;
    }
  | { kind: "event.delete"; id: string; spaceId: string; eventId: string }
  /**
   * Your own answer about one event.
   *
   * Its own kind rather than an `event.update`, because its subject is the pair
   * (event, you) and not the event: answering "иду" must not coalesce with —
   * or be dropped by — an edit to the event's title queued beside it.
   *
   * There is no user in the payload. The route is `…/participants/me` and the
   * server reads the person off the session, so this operation is incapable of
   * expressing anybody else's participation, queued or not.
   */
  | {
      kind: "event.participate";
      id: string;
      spaceId: string;
      eventId: string;
      body: SetEventParticipationRequest;
    }
  | { kind: "tag.create"; id: string; spaceId: string; body: CreateTagRequest }
  | {
      kind: "tag.update";
      id: string;
      spaceId: string;
      tagId: string;
      body: UpdateTagRequest;
    }
  | { kind: "tag.delete"; id: string; spaceId: string; tagId: string }
  /**
   * Linking is its own kind rather than an update, because its subject is the
   * pair, not the tag: two different links queued at once must both survive
   * coalescing.
   */
  | { kind: "tag.link"; id: string; spaceId: string; body: TagLinkRequest }
  | { kind: "tag.unlink"; id: string; spaceId: string; body: TagLinkRequest };

export interface QueuedOperation {
  operation: Operation;
  /** Failed attempts so far. Drives the backoff and the give-up threshold. */
  attempts: number;
  queuedAt: number;
}

/** The row an operation acts on, used for coalescing. */
function subject(operation: Operation): string {
  switch (operation.kind) {
    case "list.create":
      return `list:${operation.body.id ?? operation.id}`;
    case "list.update":
    case "list.delete":
      return `list:${operation.listId}`;
    case "item.create":
      return `item:${operation.body.id ?? operation.id}`;
    case "item.update":
    case "item.delete":
      return `item:${operation.itemId}`;
    case "wish.create":
      return `wish:${operation.body.id ?? operation.id}`;
    case "wish.update":
    case "wish.delete":
      return `wish:${operation.wishId}`;
    case "place.create":
      return `place:${operation.body.id ?? operation.id}`;
    case "place.update":
    case "place.delete":
      return `place:${operation.placeId}`;
    // The opinion, not the place: correcting a score twice before the queue
    // drains folds into one request, while an edit to the place queued
    // alongside survives untouched. This device only ever writes its own
    // opinion, so whose it is need not be in the key.
    case "place.review":
    case "place.unreview":
      return `review:${operation.placeId}`;
    case "topic.create":
      return `topic:${operation.body.id ?? operation.id}`;
    case "topic.update":
    case "topic.delete":
      return `topic:${operation.topicId}`;
    case "message.create":
      return `message:${operation.body.id ?? operation.id}`;
    case "message.update":
    case "message.delete":
      return `message:${operation.messageId}`;
    case "event.create":
      return `event:${operation.body.id ?? operation.id}`;
    case "event.update":
    case "event.delete":
      return `event:${operation.eventId}`;
    // The pair, not the event: tapping «иду» and then «не иду» before the queue
    // drains must fold into one request, while an edit to the event queued
    // alongside must survive untouched. This device only ever writes its own
    // answer, so the person is not part of the key.
    case "message.react":
      // The pair, so two people reacting to the same message queue separately —
      // and so a second tap on the same bubble replaces the first rather than
      // sending both. There is only one person in any one client's queue.
      return `reaction:${operation.messageId}`;
    case "event.participate":
      return `participant:${operation.eventId}`;
    case "tag.create":
      return `tag:${operation.body.id ?? operation.id}`;
    case "tag.update":
    case "tag.delete":
      return `tag:${operation.tagId}`;
    case "tag.link":
    case "tag.unlink":
      return `taglink:${operation.body.tagId}:${operation.body.targetType}:${operation.body.targetId}`;
  }
}

type WithBody = Extract<Operation, { body: unknown }>;

const isCreate = (operation: Operation): boolean => operation.kind.endsWith(".create");

const isDelete = (operation: Operation): boolean => operation.kind.endsWith(".delete");

/**
 * Deletes carry no patch, so only these can absorb a later update.
 *
 * Written as an `in` check rather than a kind test so the compiler narrows the
 * union — deriving it from the kind string leaves `body` unknown.
 */
const hasBody = (operation: Operation): operation is WithBody => "body" in operation;

/**
 * Fold a new operation into a pending queue.
 *
 * Returns a new array; the input is never mutated, so this stays usable as a
 * reducer and stays testable.
 */
export function enqueue(
  queue: readonly QueuedOperation[],
  operation: Operation,
  now: number = Date.now(),
): QueuedOperation[] {
  const key = subject(operation);
  const next = [...queue];

  if (isDelete(operation)) {
    const pending = next.filter((entry) => subject(entry.operation) === key);
    const createdHere = pending.some((entry) => isCreate(entry.operation));

    // A row created and deleted before either reached the server never needs to
    // exist. Dropping both is not an optimisation — sending them would leave a
    // tombstone for a row nobody else ever saw.
    const withoutSubject = next.filter((entry) => subject(entry.operation) !== key);
    if (createdHere) return withoutSubject;

    return [...withoutSubject, { operation, attempts: 0, queuedAt: now }];
  }

  // An update is the one shape that carries a patch and is not a create, so
  // this both selects it and narrows `operation` for the merge below.
  if (hasBody(operation) && !isCreate(operation)) {
    const index = next.findIndex(
      (entry) =>
        subject(entry.operation) === key &&
        entry.attempts === 0 &&
        hasBody(entry.operation),
    );

    if (index !== -1) {
      const existing = next[index]!;
      const pending = existing.operation;

      if (hasBody(pending)) {
        /*
         * Fold the patch into whatever is already queued for this row. Merging
         * into a pending create keeps the create — the row still does not exist
         * on the server, so a PATCH would 404.
         *
         * The notification follows the *later* operation, replacing whatever
         * the earlier one carried — including replacing it with nothing.
         *
         * Spreading `pending` last kept the earlier one, which is wrong in both
         * directions and only shows up offline: assign a task and change your
         * mind before the queue drains, and the other person is told about an
         * assignment that no longer exists; edit the note after assigning, and
         * the notification is dropped instead. A notification describes the
         * state that will land, and the state that lands is the last write.
         */
        const merged = {
          ...pending,
          body: { ...pending.body, ...operation.body },
          notify: "notify" in operation ? operation.notify : undefined,
        } as Operation;

        next[index] = { ...existing, operation: merged };
        return next;
      }
    }
  }

  return [...next, { operation, attempts: 0, queuedAt: now }];
}

/** Exponential backoff, capped, so a server that is down is not hammered. */
export function backoffMs(attempts: number): number {
  return Math.min(30_000, 500 * 2 ** Math.max(0, attempts - 1));
}

/**
 * Attempts after which an operation is abandoned.
 *
 * The queue must not grow forever behind one poisoned entry — a mutation the
 * server will never accept would otherwise block everything queued after it.
 */
export const MAX_ATTEMPTS = 8;

export interface FlushResult {
  queue: QueuedOperation[];
  sent: Operation[];
  /** Given up on, so the UI can tell the person their change did not stick. */
  abandoned: Operation[];
}

export type Sender = (operation: Operation) => Promise<void>;

export interface FlushOptions {
  /** Retryable failures stop the drain; permanent ones drop the entry. */
  isPermanent?: (error: unknown) => boolean;
  now?: () => number;
}

/**
 * Drain the queue in order.
 *
 * Order matters and is not an implementation detail: an item's create must
 * reach the server before the update that ticks it off, so a failure stops the
 * drain rather than skipping ahead.
 */
export async function flush(
  queue: readonly QueuedOperation[],
  send: Sender,
  options: FlushOptions = {},
): Promise<FlushResult> {
  const isPermanent = options.isPermanent ?? (() => false);

  const remaining = [...queue];
  const sent: Operation[] = [];
  const abandoned: Operation[] = [];

  while (remaining.length > 0) {
    const entry = remaining[0]!;

    try {
      await send(entry.operation);
      remaining.shift();
      sent.push(entry.operation);
      continue;
    } catch (error) {
      if (isPermanent(error)) {
        // The server will never accept this. Keeping it would block the queue.
        remaining.shift();
        abandoned.push(entry.operation);
        continue;
      }

      const attempts = entry.attempts + 1;

      if (attempts >= MAX_ATTEMPTS) {
        remaining.shift();
        abandoned.push(entry.operation);
        continue;
      }

      remaining[0] = { ...entry, attempts };
      break;
    }
  }

  return { queue: remaining, sent, abandoned };
}

/* ----------------------------------------------------------- constructors */

export const operations = {
  createList: (spaceId: string, kind: CreateListRequest["kind"], content: ContentBlob) => {
    const id = uuidv7();
    return {
      kind: "list.create" as const,
      id,
      spaceId,
      body: { id, kind, content },
    };
  },

  updateList: (spaceId: string, listId: string, body: UpdateListRequest) => ({
    kind: "list.update" as const,
    id: uuidv7(),
    spaceId,
    listId,
    body,
  }),

  deleteList: (spaceId: string, listId: string) => ({
    kind: "list.delete" as const,
    id: uuidv7(),
    spaceId,
    listId,
  }),

  createItem: (
    spaceId: string,
    body: Omit<CreateListItemRequest, "id"> & { id?: string },
    notify?: QueuedNotification,
  ) => {
    const id = body.id ?? uuidv7();
    return {
      kind: "item.create" as const,
      id,
      spaceId,
      body: { ...body, id },
      ...(notify ? { notify } : {}),
    };
  },

  updateItem: (
    spaceId: string,
    itemId: string,
    body: UpdateListItemRequest,
    notify?: QueuedNotification,
  ) => ({
    kind: "item.update" as const,
    id: uuidv7(),
    spaceId,
    itemId,
    body,
    ...(notify ? { notify } : {}),
  }),

  deleteItem: (spaceId: string, itemId: string) => ({
    kind: "item.delete" as const,
    id: uuidv7(),
    spaceId,
    itemId,
  }),

  createWish: (
    spaceId: string,
    body: Omit<CreateWishRequest, "id"> & { id?: string },
    notify?: QueuedNotification,
  ) => {
    const id = body.id ?? uuidv7();
    return {
      kind: "wish.create" as const,
      id,
      spaceId,
      body: { ...body, id },
      ...(notify ? { notify } : {}),
    };
  },

  updateWish: (spaceId: string, wishId: string, body: UpdateWishRequest) => ({
    kind: "wish.update" as const,
    id: uuidv7(),
    spaceId,
    wishId,
    body,
  }),

  deleteWish: (spaceId: string, wishId: string) => ({
    kind: "wish.delete" as const,
    id: uuidv7(),
    spaceId,
    wishId,
  }),

  createPlace: (
    spaceId: string,
    body: Omit<CreatePlaceRequest, "id"> & { id?: string },
    notify?: QueuedNotification,
  ) => {
    const id = body.id ?? uuidv7();
    return {
      kind: "place.create" as const,
      id,
      spaceId,
      body: { ...body, id },
      ...(notify ? { notify } : {}),
    };
  },

  updatePlace: (spaceId: string, placeId: string, body: UpdatePlaceRequest) => ({
    kind: "place.update" as const,
    id: uuidv7(),
    spaceId,
    placeId,
    body,
  }),

  deletePlace: (spaceId: string, placeId: string) => ({
    kind: "place.delete" as const,
    id: uuidv7(),
    spaceId,
    placeId,
  }),

  reviewPlace: (spaceId: string, placeId: string, body: PutPlaceReviewRequest) => ({
    kind: "place.review" as const,
    id: uuidv7(),
    spaceId,
    placeId,
    body,
  }),

  unreviewPlace: (spaceId: string, placeId: string) => ({
    kind: "place.unreview" as const,
    id: uuidv7(),
    spaceId,
    placeId,
  }),

  createTopic: (spaceId: string, body: Omit<CreateTopicRequest, "id"> & { id?: string }) => {
    const id = body.id ?? uuidv7();
    return { kind: "topic.create" as const, id, spaceId, body: { ...body, id } };
  },

  updateTopic: (spaceId: string, topicId: string, body: UpdateTopicRequest) => ({
    kind: "topic.update" as const,
    id: uuidv7(),
    spaceId,
    topicId,
    body,
  }),

  deleteTopic: (spaceId: string, topicId: string) => ({
    kind: "topic.delete" as const,
    id: uuidv7(),
    spaceId,
    topicId,
  }),

  createMessage: (
    spaceId: string,
    body: Omit<CreateMessageRequest, "id"> & { id?: string },
    notify?: QueuedNotification,
  ) => {
    const id = body.id ?? uuidv7();
    return {
      kind: "message.create" as const,
      id,
      spaceId,
      body: { ...body, id },
      ...(notify ? { notify } : {}),
    };
  },

  updateMessage: (spaceId: string, messageId: string, body: UpdateMessageRequest) => ({
    kind: "message.update" as const,
    id: uuidv7(),
    spaceId,
    messageId,
    body,
  }),

  deleteMessage: (spaceId: string, messageId: string) => ({
    kind: "message.delete" as const,
    id: uuidv7(),
    spaceId,
    messageId,
  }),

  /** Your own reaction, and only ever your own — see the operation's note. */
  react: (spaceId: string, messageId: string, body: SetReactionRequest) => ({
    kind: "message.react" as const,
    id: uuidv7(),
    spaceId,
    messageId,
    body,
  }),

  createEvent: (spaceId: string, body: Omit<CreateEventRequest, "id"> & { id?: string }) => {
    const id = body.id ?? uuidv7();
    return { kind: "event.create" as const, id, spaceId, body: { ...body, id } };
  },

  updateEvent: (spaceId: string, eventId: string, body: UpdateEventRequest) => ({
    kind: "event.update" as const,
    id: uuidv7(),
    spaceId,
    eventId,
    body,
  }),

  deleteEvent: (spaceId: string, eventId: string) => ({
    kind: "event.delete" as const,
    id: uuidv7(),
    spaceId,
    eventId,
  }),

  /** Your own answer, and only ever your own — see the operation's note. */
  participate: (
    spaceId: string,
    eventId: string,
    body: SetEventParticipationRequest,
  ) => ({
    kind: "event.participate" as const,
    id: uuidv7(),
    spaceId,
    eventId,
    body,
  }),

  createTag: (spaceId: string, body: Omit<CreateTagRequest, "id"> & { id?: string }) => {
    const id = body.id ?? uuidv7();
    return { kind: "tag.create" as const, id, spaceId, body: { ...body, id } };
  },

  updateTag: (spaceId: string, tagId: string, body: UpdateTagRequest) => ({
    kind: "tag.update" as const,
    id: uuidv7(),
    spaceId,
    tagId,
    body,
  }),

  deleteTag: (spaceId: string, tagId: string) => ({
    kind: "tag.delete" as const,
    id: uuidv7(),
    spaceId,
    tagId,
  }),

  linkTag: (spaceId: string, body: TagLinkRequest) => ({
    kind: "tag.link" as const,
    id: uuidv7(),
    spaceId,
    body,
  }),

  unlinkTag: (spaceId: string, body: TagLinkRequest) => ({
    kind: "tag.unlink" as const,
    id: uuidv7(),
    spaceId,
    body,
  }),
};
